import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  AppShell,
  ContextSwitcher,
  ClassificationBadge,
  StageBanner,
  ThemeToggle,
  useTheme,
  type NavGroup,
} from '@verydia/ui';
import {
  Boxes,
  BookOpen,
  Building,
  Building2,
  Cable,
  FileClock,
  Gauge,
  KeyRound,
  Layers,
  LayoutDashboard,
  Package,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  UsersRound,
  Webhook,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useConsoleContext } from '../lib/console-context.tsx';
import {
  NEUTRAL_ORGANIZATION_ROUTE,
  isProductScopedRoute,
  productRouteAfterSwitch,
} from '../lib/context-routing.ts';
import { UserMenu } from './user-menu.tsx';

/**
 * Navigation mirrors §7 exactly. The grouping is the point: resources a vendor
 * manages, tools a developer integrates with, and platform-level records are three
 * different jobs, and flattening them into one list is how consoles end up with
 * fourteen equally-weighted links.
 */
const NAVIGATION: readonly NavGroup[] = [
  {
    items: [{ label: 'Overview', href: '/', icon: <LayoutDashboard /> }],
  },
  {
    label: 'Resources',
    items: [
      { label: 'Team', href: '/team', icon: <Users /> },
      { label: 'Products', href: '/products', icon: <Package /> },
      { label: 'Customers', href: '/customers', icon: <Building /> },
      { label: 'Licenses', href: '/licenses', icon: <ShieldCheck /> },
      { label: 'Usage', href: '/usage', icon: <Gauge /> },
      { label: 'Analytics', href: '/analytics', icon: <Layers /> },
    ],
  },
  {
    label: 'Developers',
    items: [
      { label: 'API Keys', href: '/developers/api-keys', icon: <KeyRound /> },
      { label: 'Webhooks', href: '/developers/webhooks', icon: <Webhook /> },
      { label: 'Request Logs', href: '/developers/request-logs', icon: <FileClock /> },
      { label: 'Environments', href: '/developers/environments', icon: <Boxes /> },
      { label: 'SDKs', href: '/developers/sdks', icon: <Cable /> },
    ],
  },
  {
    label: 'Platform',
    items: [
      { label: 'Organizations', href: '/platform/organizations', icon: <Building2 /> },
      { label: 'People', href: '/platform/users', icon: <UsersRound /> },
      { label: 'Audit Log', href: '/audit', icon: <ScrollText /> },
      { label: 'Settings', href: '/settings', icon: <Settings /> },
    ],
  },
];

export function ConsoleShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const theme = useTheme();
  const context = useConsoleContext();

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`);

  return (
    <AppShell
      navigation={NAVIGATION}
      linkComponent={Link}
      isActive={isActive}
      /*
        Deliberately says nothing about which features exist.

        It used to enumerate them by internal milestone — "VC-2 · product
        catalog", "customers arrive in later stages" — which made the banner
        wrong the moment a milestone shipped, and turned engineering scheduling
        into what customers read as product truth. That copy has gone stale in
        production more than once.

        What the platform can do is answered in exactly one place: the capability
        manifest on the Overview, from the API. This says only that the product
        is pre-release, which stays true without maintenance.
      */
      banner={
        <StageBanner>
          <strong className="font-semibold">Preview.</strong> Verydia Control is in preview. Some
          platform capabilities are not yet available — the Overview lists what this deployment
          currently provides.
        </StageBanner>
      }
      contextBar={
        <>
          <ContextSwitcher
            kind="Organization"
            value={
              context.organization
                ? {
                    id: context.organization.id,
                    label: context.organization.name,
                    description: context.organization.note,
                  }
                : null
            }
            options={context.organizations.map((organization) => ({
              id: organization.id,
              label: organization.name,
              description: organization.note,
            }))}
            onSelect={(id) => {
              context.selectOrganization(id);
              // The product underneath belonged to the organization being left,
              // so a product-scoped URL is stale the moment this runs.
              if (isProductScopedRoute(pathname)) {
                void navigate({ to: NEUTRAL_ORGANIZATION_ROUTE });
              }
            }}
            placeholder="No organizations"
            unavailableReason={
              context.organizations.length <= 1
                ? 'You belong to one organization. The switcher activates when you are a member of more than one.'
                : undefined
            }
          />

          <ContextSeparator />

          {/*
            Real, from the same catalog the Products page reads. It said
            "No products yet" and "Products can be created from VC-2" — both
            true when written, both false the moment a product exists.
          */}
          <ContextSwitcher
            kind="Product"
            value={
              context.product
                ? {
                    id: context.product.id,
                    label: context.product.name,
                    description: context.product.slug,
                  }
                : null
            }
            options={context.products.map((product) => ({
              id: product.id,
              label: product.name,
              description: product.slug,
            }))}
            onSelect={(id) => {
              context.selectProduct(id);
              const destination = productRouteAfterSwitch(pathname, id);
              if (destination !== null) void navigate({ to: destination });
            }}
            /*
              Three different states, said three different ways.

              An organization with one archived product is not an organization
              with no products. Collapsing the two read "No products yet" on a
              page that was simultaneously listing one, which is the same class
              of untruth as the fabricated environments this bar used to show.
            */
            placeholder={
              context.products.length > 0
                ? 'No product selected'
                : context.hasAnyProducts
                  ? 'No active products'
                  : 'No products yet'
            }
            loading={context.productsLoading}
            {...(context.products.length === 0 && !context.productsLoading
              ? {
                  unavailableReason: context.hasAnyProducts
                    ? 'Every product in this organization is archived.'
                    : 'Create a product to select one here.',
                }
              : {})}
          />

          <ContextSeparator />

          {/*
            Environments belong to a product. With none selected there is nothing
            for one to belong to, so this offers no choice rather than a choice
            that cannot be valid.
          */}
          <ContextSwitcher
            kind="Environment"
            value={
              context.environment
                ? {
                    id: context.environment.id,
                    label: context.environment.name,
                    badge: (
                      <ClassificationBadge classification={context.environment.classification} />
                    ),
                  }
                : null
            }
            options={context.environments.map((environment) => ({
              id: environment.id,
              label: environment.name,
              badge: <ClassificationBadge classification={environment.classification} />,
            }))}
            onSelect={context.selectEnvironment}
            onClear={context.clearEnvironment}
            clearLabel="No environment"

            placeholder={
              /*
                Two different facts. "None selected" describes the selection;
                "no active environments" describes availability. Saying the
                first when the second is true tells the operator to go and pick
                one from a list that is empty.
              */
              context.environments.length === 0 && context.product !== null
                ? 'No active environments'
                : 'No environment selected'
            }
            /*
              Only when there is genuinely nothing to choose.

              This used to pass a reason unconditionally, and `ContextSwitcher`
              treats any reason as "disabled" — so the selector was permanently
              inert. It looked correct for as long as no environments existed,
              which was the whole of Checkpoint A and B: a control that is
              supposed to be disabled, and a bug that always disables it, are
              indistinguishable until the data arrives.
            */
            loading={context.environmentsLoading}
            {...(context.environments.length === 0 && !context.environmentsLoading
              ? {
                  unavailableReason:
                    context.product === null
                      ? 'Select a product first — environments belong to a product.'
                      : 'This product has no active environments.',
                }
              : {})}
          />
        </>
      }
      topBarActions={
        <>
          {/*
            Points at the in-console SDK page, not at
            docs.control.parallax-motion.com. That host does not exist yet — it
            sits under a subdomain that is not delegated — and a top-bar link
            that resolves nowhere is worse than no link at all. It moves to the
            documentation site when that site ships.
          */}
          <Link
            to="/developers/sdks"
            className={
              'flex h-7 items-center gap-1.5 rounded-[var(--vc-radius-md)] px-2 ' +
              'text-[12.5px] text-[var(--vc-text-muted)] transition-colors ' +
              'duration-[var(--vc-duration-fast)] hover:bg-[var(--vc-surface-hover)] ' +
              'hover:text-[var(--vc-text)] focus-visible:outline-2 focus-visible:outline-offset-2 ' +
              'focus-visible:outline-[var(--vc-focus-ring)]'
            }
          >
            <BookOpen aria-hidden="true" className="size-3.5" />
            Docs
          </Link>
          <ThemeToggle preference={theme.preference} onCycle={theme.cycle} />
          <UserMenu />
        </>
      }
    >
      {children}
    </AppShell>
  );
}

function ContextSeparator() {
  return (
    <span aria-hidden="true" className="px-0.5 text-[var(--vc-text-subtle)] select-none">
      /
    </span>
  );
}
